import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import Axios, { type AxiosResponse } from "axios";
import { KMainFunctions } from "../shared/utility/main-functions";

function rethrowOrHandle(error: unknown): never {
  if (Axios.isCancel(error)) {
    throw error;
  }
  KMainFunctions.exceptionHandler(error);
  throw error;
}

const backendUrl = CommonHelperFunctions.GetBackendUrl();

export interface AIBalanceResponse {
  totalPurchasedCents: number;
  totalGrantedCredits?: number;
  totalSpentUsd: number;
  creditsConsumed: number;
  creditsRemaining: number;
  starterCreditsIncluded: number;
  balanceRevision?: string;
}

export interface AIPaymentRow {
  id: string;
  amount: number;
  stripeSessionId: string | null;
  paymentStatus: string;
  description: string | null;
  isManualEntry: boolean;
  createdAt: string;
}

export interface AIPaymentAdminRow {
  id: string;
  createdAt: string;
  domainId: string;
  domainName: string;
  amountCents: number;
  credits: number;
  paymentStatus: string;
  isManualEntry: boolean;
  stripeSessionId: string | null;
  description: string | null;
  createdByEmail: string | null;
}

export interface AIDomainSummary {
  domainId: string;
  domainName: string;
  totalPurchasedCents: number;
  lifetimeSpentUsd: number;
  creditsRemaining: number;
  lastActivityAt: string | null;
  topUserEmail: string | null;
}

export interface AIPaymentAdminResponse {
  rows: AIPaymentAdminRow[];
  domainSummaries: AIDomainSummary[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AIDomainLookupRow {
  domainId: string;
  domainName: string;
}

export interface AIChatUsageAdminRow {
  id: string;
  requestId: string | null;
  createdAt: string;
  domainId: string;
  domainName: string | null;
  userEmail: string | null;
  model: string | null;
  tokensInput: number;
  tokensOutput: number;
  costUsd: number;
  creditsConsumed: number;
  reconciled: boolean;
}

export interface AIChatUsageAdminResponse {
  rows: AIChatUsageAdminRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AIKpiSnapshot {
  monthRevenueCents: number;
  monthCreditsGranted: number;
  monthCreditsBurned: number;
  monthActiveDomains: number;
  lifetimeRevenueCents: number;
  lifetimeCreditsBurned: number;
}

export interface AIDailyBurnPoint {
  date: string;
  credits: number;
  costUsd: number;
  chatCount: number;
  tokensInput: number;
  tokensOutput: number;
}

export interface AITopDomainRow {
  domainId: string;
  domainName: string | null;
  creditsBurned: number;
  chatCount: number;
  costUsd: number;
  tokensInput: number;
  tokensOutput: number;
  totalPurchasedCents: number;
  totalGrantedCredits: number;
  creditsRemaining: number;
  burnRateCreditsPerDay: number;
  daysToZero: number | null;
  lastActivityAt: string | null;
}

export interface AITopUserRow {
  userId: string;
  userEmail: string | null;
  domainId: string | null;
  domainName: string | null;
  creditsBurned: number;
  chatCount: number;
  costUsd: number;
  tokensInput: number;
  tokensOutput: number;
  avgCreditsPerRequest: number;
}

export interface AIModelBreakdownRow {
  model: string;
  creditsBurned: number;
  chatCount: number;
  costUsd: number;
  tokensInput: number;
  tokensOutput: number;
  avgCreditsPerRequest: number;
  avgCostPerRequest: number;
  outputTokenRatio: number;
  percentage: number;
}

export interface AIReconciliationHealth {
  totalRequests: number;
  reconciled: number;
  ratePercent: number;
}

export interface AITopExpensiveRequest {
  id: string;
  requestId: string | null;
  createdAt: string;
  domainName: string | null;
  userEmail: string | null;
  model: string | null;
  creditsConsumed: number;
  tokensInput: number;
  tokensOutput: number;
}

export interface AIAdminAnalyticsResponse {
  kpis: AIKpiSnapshot;
  summary: AIAdminMetricSummary;
  from: string | null;
  to: string | null;
  range: string;
  dailyBurn: AIDailyBurnPoint[];
  topDomains: AITopDomainRow[];
  topUsers: AITopUserRow[];
  modelBreakdown: AIModelBreakdownRow[];
  reconciliation: AIReconciliationHealth;
  topExpensiveRequests: AITopExpensiveRequest[];
}

export interface AIAdminMetricSummary {
  creditsBurned: number;
  costUsd: number;
  chatCount: number;
  tokensInput: number;
  tokensOutput: number;
  activeDomains: number;
  activeUsers: number;
  avgCostPerRequest: number;
  avgCreditsPerRequest: number;
  avgTokensPerRequest: number;
}

export interface AIAdminBreakdownRow {
  id: string;
  domainId: string | null;
  domainName: string | null;
  userId: string | null;
  userEmail: string | null;
  model: string | null;
  creditsBurned: number;
  costUsd: number;
  chatCount: number;
  tokensInput: number;
  tokensOutput: number;
  totalPurchasedCents: number;
  totalGrantedCredits: number;
  creditsRemaining: number;
  avgCreditsPerRequest: number;
  avgCostPerRequest: number;
  burnRateCreditsPerDay: number;
  daysToZero: number | null;
  lastActivityAt: string | null;
}

export interface AIAdminBreakdownResponse {
  rows: AIAdminBreakdownRow[];
  total: number;
  page: number;
  pageSize: number;
  dimension: string;
}

export interface AIAdminDomainDetailResponse {
  domainId: string;
  domainName: string | null;
  totalPurchasedCents: number;
  totalGrantedCredits: number;
  creditsConsumed: number;
  creditsRemaining: number;
  costUsd: number;
  chatCount: number;
  tokensInput: number;
  tokensOutput: number;
  burnRateCreditsPerDay: number;
  daysToZero: number | null;
  lastActivityAt: string | null;
  dailyBurn: AIDailyBurnPoint[];
  topUsers: AITopUserRow[];
  modelBreakdown: AIModelBreakdownRow[];
  latestRequests: AIChatUsageAdminRow[];
  ledgerPreview: AIAdminLedgerRow[];
}

export interface AIAdminRequestDetailResponse extends AIChatUsageAdminRow {
  totalTokens: number;
  outputTokenRatio: number;
}

export interface AIAdminLedgerRow {
  id: string;
  createdAt: string;
  domainId: string;
  domainName: string | null;
  type: string;
  creditsDelta: number;
  amountCents: number | null;
  costUsd: number | null;
  description: string | null;
  referenceId: string | null;
  userEmail: string | null;
  model: string | null;
  reconciled: boolean | null;
}

export interface AIAdminLedgerResponse {
  rows: AIAdminLedgerRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AIPaymentAdminQuery {
  search?: string;
  domainId?: string;
  from?: string;
  to?: string;
  range?: string;
  sortBy?: string;
  sortDirection?: string;
  page?: number;
  pageSize?: number;
  excludeInternal?: boolean;
}

export interface AIChatUsageAdminQuery {
  domainId?: string;
  userId?: string;
  from?: string;
  to?: string;
  search?: string;
  model?: string;
  range?: string;
  sortBy?: string;
  sortDirection?: string;
  page?: number;
  pageSize?: number;
  excludeInternal?: boolean;
}

export interface AIAdminAnalyticsQuery {
  range?: string;
  from?: string;
  to?: string;
  domainId?: string;
  userId?: string;
  model?: string;
  dailyBurnDays?: number;
  excludeInternal?: boolean;
}

export interface AIAdminBreakdownQuery {
  dimension: "domain" | "user" | "model";
  range?: string;
  from?: string;
  to?: string;
  domainId?: string;
  userId?: string;
  model?: string;
  sortBy?: string;
  sortDirection?: string;
  page?: number;
  pageSize?: number;
  excludeInternal?: boolean;
}

export interface AIAdminDomainDetailQuery {
  range?: string;
  from?: string;
  to?: string;
  excludeInternal?: boolean;
}

export interface AIAdminLedgerQuery {
  domainId: string;
  page?: number;
  pageSize?: number;
  excludeInternal?: boolean;
}

const EMPTY_ANALYTICS: AIAdminAnalyticsResponse = {
  kpis: {
    monthRevenueCents: 0,
    monthCreditsGranted: 0,
    monthCreditsBurned: 0,
    monthActiveDomains: 0,
    lifetimeRevenueCents: 0,
    lifetimeCreditsBurned: 0
  },
  summary: {
    creditsBurned: 0,
    costUsd: 0,
    chatCount: 0,
    tokensInput: 0,
    tokensOutput: 0,
    activeDomains: 0,
    activeUsers: 0,
    avgCostPerRequest: 0,
    avgCreditsPerRequest: 0,
    avgTokensPerRequest: 0
  },
  from: null,
  to: null,
  range: "30d",
  dailyBurn: [],
  topDomains: [],
  topUsers: [],
  modelBreakdown: [],
  reconciliation: { totalRequests: 0, reconciled: 0, ratePercent: 0 },
  topExpensiveRequests: []
};

export class AiPaymentService {
  public static async GetBalance(): Promise<AxiosResponse<AIBalanceResponse>> {
    return Axios.get<AIBalanceResponse>(`${backendUrl}/Payment/ai-balance`);
  }

  public static async GetTotalPurchased(): Promise<AxiosResponse<{ totalAmountInCents: number }>> {
    return Axios.get<{ totalAmountInCents: number }>(`${backendUrl}/Payment/ai-payment/total`);
  }

  public static async CreateCheckout(amount: number, successUrl: string, cancelUrl: string, description?: string) {
    return Axios.post<string>(`${backendUrl}/Payment/ai-payment/checkout`, {
      amount,
      successUrl,
      cancelUrl,
      description
    });
  }

  public static async VerifyPayment(sessionId: string) {
    return Axios.post<AIPaymentRow>(`${backendUrl}/Payment/ai-payment/verify`, { sessionId });
  }

  public static async SyncPayments() {
    return Axios.post<{ syncedCount: number; skippedCount: number }>(`${backendUrl}/Payment/ai-payment/sync`);
  }

  public static async CheckAdmin(): Promise<boolean> {
    try {
      const res = await Axios.get<{ isAdmin: boolean }>(`${backendUrl}/Payment/ai-payment/admin/check`);
      return res.data?.isAdmin === true;
    } catch {
      return false;
    }
  }

  public static async GetAdminAllPayments(
    query?: AIPaymentAdminQuery,
    signal?: AbortSignal
  ): Promise<AIPaymentAdminResponse> {
    try {
      const res = await Axios.get<AIPaymentAdminResponse>(`${backendUrl}/Payment/ai-payment/admin/all`, {
        params: query,
        signal
      });
      return res.data;
    } catch (error: unknown) {
      return rethrowOrHandle(error);
    }
  }

  public static async SearchDomains(search?: string, limit = 20): Promise<AIDomainLookupRow[]> {
    try {
      const res = await Axios.get<AIDomainLookupRow[]>(`${backendUrl}/Payment/ai-payment/admin/domains`, {
        params: { search, limit }
      });
      return res.data || [];
    } catch (error: unknown) {
      KMainFunctions.exceptionHandler(error);
      return [];
    }
  }

  public static async CreateManualPayment(domainId: string, amount: number, description?: string) {
    return Axios.post(`${backendUrl}/Payment/ai-payment/manual`, {
      domainId,
      amount,
      description
    });
  }

  public static async GetAdminAllUsage(
    params?: AIChatUsageAdminQuery,
    signal?: AbortSignal
  ): Promise<AIChatUsageAdminResponse> {
    try {
      const res = await Axios.get<AIChatUsageAdminResponse>(`${backendUrl}/Payment/ai-usage/admin/all`, {
        params,
        signal
      });
      return res.data;
    } catch (error: unknown) {
      return rethrowOrHandle(error);
    }
  }

  public static async GetAdminAnalytics(
    params?: AIAdminAnalyticsQuery,
    signal?: AbortSignal
  ): Promise<AIAdminAnalyticsResponse> {
    try {
      const res = await Axios.get<AIAdminAnalyticsResponse>(`${backendUrl}/Payment/ai-usage/admin/analytics`, {
        params,
        signal
      });
      return res.data || EMPTY_ANALYTICS;
    } catch (error: unknown) {
      return rethrowOrHandle(error);
    }
  }

  public static async GetAdminBreakdown(
    params: AIAdminBreakdownQuery,
    signal?: AbortSignal
  ): Promise<AIAdminBreakdownResponse> {
    try {
      const res = await Axios.get<AIAdminBreakdownResponse>(`${backendUrl}/Payment/ai-usage/admin/breakdown`, {
        params,
        signal
      });
      return res.data;
    } catch (error: unknown) {
      return rethrowOrHandle(error);
    }
  }

  public static async GetAdminDomainDetail(
    domainId: string,
    params?: AIAdminDomainDetailQuery,
    signal?: AbortSignal
  ): Promise<AIAdminDomainDetailResponse | null> {
    try {
      const res = await Axios.get<AIAdminDomainDetailResponse>(
        `${backendUrl}/Payment/ai-usage/admin/domains/${domainId}/detail`,
        { params, signal }
      );
      return res.data;
    } catch (error: unknown) {
      return rethrowOrHandle(error);
    }
  }

  public static async GetAdminRequestDetail(
    requestId: string,
    signal?: AbortSignal
  ): Promise<AIAdminRequestDetailResponse | null> {
    try {
      const res = await Axios.get<AIAdminRequestDetailResponse>(
        `${backendUrl}/Payment/ai-usage/admin/requests/${requestId}/detail`,
        { signal }
      );
      return res.data;
    } catch (error: unknown) {
      return rethrowOrHandle(error);
    }
  }

  public static async GetAdminLedger(params: AIAdminLedgerQuery, signal?: AbortSignal): Promise<AIAdminLedgerResponse> {
    try {
      const res = await Axios.get<AIAdminLedgerResponse>(`${backendUrl}/Payment/ai-usage/admin/ledger`, {
        params,
        signal
      });
      return res.data;
    } catch (error: unknown) {
      KMainFunctions.exceptionHandler(error);
      throw error;
    }
  }

  // ── Workspace analytics (owner-only, KOA-18066) ────────────────────
  // Backed by v1/api/AIWorkspace — domain-scoped + CheckDomainOwner-gated
  // server side, unlike the ai-usage/admin/* internal surface above.

  public static async GetWorkspaceAnalytics(
    query?: AIWorkspaceAnalyticsQuery,
    signal?: AbortSignal
  ): Promise<AIWorkspaceAnalyticsResponse> {
    try {
      const res = await Axios.get<AIWorkspaceAnalyticsResponse>(`${backendUrl}/AIWorkspace/analytics`, {
        params: query,
        signal
      });
      return res.data;
    } catch (error: unknown) {
      return rethrowOrHandle(error);
    }
  }

  public static async GetWorkspaceRequests(
    query?: AIWorkspaceRequestsQuery,
    signal?: AbortSignal
  ): Promise<AIWorkspaceRequestsResponse> {
    try {
      const res = await Axios.get<AIWorkspaceRequestsResponse>(`${backendUrl}/AIWorkspace/requests`, {
        params: query,
        signal
      });
      return res.data;
    } catch (error: unknown) {
      return rethrowOrHandle(error);
    }
  }
}

/**
 * AI credit payments are enabled in every environment. Previously the flow
 * was gated to platform.kuika.com only; that gate was removed so localhost
 * and staging can exercise the same credit enforcement path.
 */
export function isAiPaymentEnabled(): boolean {
  return true;
}

// ── Workspace analytics DTOs (owner-only, KOA-18066) ───────────────────────
// Mirror Backend AIWorkspaceModels.cs. Deliberately NO USD fields — the
// end-user surface is credits-only (same philosophy as the credits panel).

export type AIWorkspaceRange = "7d" | "30d" | "90d" | "mtd" | "lifetime";

export interface AIWorkspaceAnalyticsQuery {
  range?: AIWorkspaceRange;
  from?: string;
  to?: string;
  projectId?: string;
  userId?: string;
  model?: string;
}

export interface AIWorkspaceKpis {
  creditsConsumed: number;
  requestCount: number;
  activeUsers: number;
  tokensInput: number;
  tokensOutput: number;
}

export interface AIWorkspaceTrendPoint {
  /** UTC day, "yyyy-MM-dd". */
  date: string;
  creditsConsumed: number;
  requestCount: number;
}

export interface AIWorkspaceUserBreakdownRow {
  userId: string | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  profilePhotoUrl: string | null;
  creditsConsumed: number;
  requestCount: number;
  tokensInput: number;
  tokensOutput: number;
  lastActivityAt: string | null;
}

export interface AIWorkspaceProjectBreakdownRow {
  /** Null = rows recorded before project attribution shipped ("Unattributed"). */
  projectId: string | null;
  projectName: string | null;
  creditsConsumed: number;
  requestCount: number;
  tokensInput: number;
  tokensOutput: number;
  lastActivityAt: string | null;
}

export interface AIWorkspaceModelBreakdownRow {
  model: string | null;
  creditsConsumed: number;
  requestCount: number;
  tokensInput: number;
  tokensOutput: number;
}

export interface AIWorkspaceAnalyticsResponse {
  kpis: AIWorkspaceKpis;
  dailyTrend: AIWorkspaceTrendPoint[];
  byUser: AIWorkspaceUserBreakdownRow[];
  byProject: AIWorkspaceProjectBreakdownRow[];
  byModel: AIWorkspaceModelBreakdownRow[];
  windowFrom: string;
  windowTo: string;
  range: string;
}

export interface AIWorkspaceRequestsQuery {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: string;
  search?: string;
  projectId?: string;
  userId?: string;
  model?: string;
  range?: AIWorkspaceRange;
  from?: string;
  to?: string;
}

export interface AIWorkspaceRequestRow {
  id: string;
  requestId: string | null;
  createdAt: string;
  userId: string | null;
  userEmail: string | null;
  projectId: string | null;
  projectName: string | null;
  model: string | null;
  tokensInput: number;
  tokensOutput: number;
  creditsConsumed: number;
  reconciled: boolean;
}

export interface AIWorkspaceRequestsResponse {
  rows: AIWorkspaceRequestRow[];
  total: number;
  page: number;
  pageSize: number;
}
